import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Spinner} from '../../Spinner';
import {Switch} from '../../buttons/Swtich';
import {LoadErrorState} from '../../elements/LoadErrorState';
import BudgetValueInput from '../../elements/BudgetValueInput';
import BudgetPartialGoalsConfiguration from '../../elements/BudgetPartialGoalsConfiguration';
import {AddBusinessUnitModal} from '../../client/AddBusinessUnitModal';
import {BusinessUnitPeriodField, BusinessUnitPeriodFieldClass} from '../../client/BusinessUnitPeriodField';
import {GoalConfiguration} from '../../business_units/global_info/GoalConfiguration';
import {AdAccountsActionDataHandler} from '../../AdAccountsActionDataHandler';
import {goalIsComplete} from '../../client/panel_v11/LineGoalModal';
import {LOCALE as AMOUNT_LOCALE} from '../../client/panel_v11/panelFormatters';
import {ArrowLeft, ArrowRight, CaretDown} from '../../icons';
import {BU_PERIOD_PERIODIC, BU_PERIOD_TEMPORARY} from '../../../periods';
import {DatetimeHelper} from '../../../utils/DatetimeHelper';
import {useLineWizard, wizardPath} from '../LineWizardScreen';
import {CLIENT_MODE_EXISTING, WEEKDAYS, clearDraft, scheduleIsCustom} from '../lineWizardDraft';
import {fetchAgencyData, fetchClientBasicData} from '../lineWizardApi';

/**
 * Paso 2 · La línea y sus objetivos.
 *
 * Tres decisiones del mockup y del feedback de Pablo que este paso sostiene:
 *
 * - **El presupuesto es SÓLO global.** El presupuesto por campaña sale del armado (acordado el
 *   29/07 y reconfirmado el 22/09): se carga después, como hoy los objetivos por campaña. Por eso
 *   no hay selector de tipo de línea: toda línea del wizard nace `TYPE_ONE_BUDGET_FOR_ALL`.
 *   Quien necesita una línea POR CAMPAÑA o POR GRUPO (22 % de las líneas, y más de lo nuevo) tiene
 *   una salida debajo del presupuesto al editor completo del clásico, que sí tiene el selector de
 *   tipo (decisión D3 de fede, 02/10; el soporte de verdad es del wizard v2).
 * - **Los días de corrida van en «Opciones avanzadas», plegadas al pie.** Estuvieron a la vista y
 *   confundían: sólo sirven para que el ritmo se mida sin los días en que las campañas no corren, y
 *   casi nadie los usa (QA de fede, 02/10). Se despliegan solas si ya hay días elegidos.
 * - **El período mensual arranca siempre el día 1**: el selector del día del mes no se ofrece acá
 *   (QA de fede, 02/10). Quien lo necesita lo tiene en el editor completo.
 * - **Objetivo 1 y 2 se definen acá**, con el mismo picker del clásico (`GoalConfiguration`). La
 *   línea todavía no existe, así que el picker trabaja sin sus cuentas: las métricas de conversión
 *   quedan con «usar los eventos de la línea», que se eligen en el paso 4.
 *
 * Entrando desde un cliente este es el PRIMER paso (no hay paso «Cliente»): el encabezado nombra al
 * cliente, explica qué es una línea —el único lugar que lo decía era el paso 1— y «Volver» sale del
 * wizard en vez de ir a un paso que no existe.
 */
export function StepLine() {
    const {t, i18n} = useTranslation();
    // Los montos con el formato del Panel 2.1 («100.000»); las fechas y el calendario en el idioma de
    // la pantalla. Los componentes del clásico que se montan acá los reciben como props opcionales.
    const dateLocale = i18n.language || 'es';
    const navigate = useNavigate();
    const {draft, update, leave} = useLineWizard();
    const [defaults, setDefaults] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(() => !!draft.line.schedule.enabled);
    // El editor completo del clásico es un modal de clase que se abre por `ref` (como en la ficha
    // clásica y el Tablero): se abre ENCIMA del wizard, así que cerrarlo devuelve al mismo lugar.
    const fullEditor = useRef(null);
    const line = draft.line;
    const locked = !!draft.created.businessUnitId;
    const fixedClient = !!draft.client.fixed;
    const existingClientId = draft.client.mode === CLIENT_MODE_EXISTING ? draft.client.id : null;

    // El picker de objetivos pide las cuentas de la línea para ofrecer sus eventos. Sin línea no
    // hay cuentas: se le pasa un handler vacío para que no vaya a buscarlas.
    const emptyActionData = useMemo(() => new AdAccountsActionDataHandler([], t), [t]);

    const load = () => {
        setLoadError(null);
        setDefaults(null);
        const clientId = existingClientId;
        const request = clientId
            ? fetchClientBasicData(clientId).then(response => response?.status === 'OK' ? response.data : null)
            : fetchAgencyData().then(response => response?.status === 'OK' ? response.general_data : null);
        request.then(data => {
            if (!data) {
                setLoadError('line_wizard.load_error');
                return;
            }
            setDefaults({
                currency: data.default_currency,
                canEditCurrency: !!data.can_edit_currency,
                // Sólo del cliente: sin cliente, `general_data` es la AGENCIA y su `name` no es el de nadie acá.
                clientName: clientId ? (data.name || '') : '',
            });
        });
    };

    useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setLine = (changes) => update(current => ({...current, line: {...current.line, ...changes}}));

    const period = line.period || BusinessUnitPeriodFieldClass.defaultSelectedPeriod();
    const currency = line.currency || (defaults && defaults.currency);
    const {firstDate, lastDate} = periodDates(period);

    const nameOk = line.name.trim().length > 0;
    const budgetOk = Number(line.budget) > 0;
    const goalsOk = line.goals.every(goal => goalIsComplete(goal));
    const daysOk = !line.schedule.enabled || WEEKDAYS.some(day => line.schedule.days[day]);
    const valid = nameOk && budgetOk && goalsOk && daysOk;
    const advancedVisible = advancedOpen || (showErrors && !daysOk);

    const next = () => {
        if (!valid) {
            setShowErrors(true);
            return;
        }
        if (!line.currency && currency) setLine({currency});
        navigate(wizardPath('cuentas'));
    };

    if (loadError !== null) {
        return (
            <div className="lw-step">
                <LoadErrorState title={t(loadError)} text={t('line_wizard.load_error_explanation')}
                                retryLabel={t('line_wizard.load_error_retry')} onRetry={load}/>
            </div>
        );
    }
    if (defaults === null) return <div className="box-loader"><Spinner color="positive"/></div>;

    const clientName = defaults.clientName || draft.client.name;

    // Lo creado en el editor completo es otra línea, armada allá: el borrador de acá se descarta y se
    // sigue en la línea nueva, igual que en la ficha clásica (cuentas, con el modal de agregar abierto).
    const afterFullEditor = businessUnitId => {
        clearDraft();
        navigate(`/business-unit/${businessUnitId}/accounts?showAddAccountModal=true`);
    };

    return (
        <div className="lw-step">
            <div className="ob-head">
                {fixedClient && clientName && <div className="lw-head-client">{clientName}</div>}
                <h1>{t('line_wizard.line.title')}</h1>
                {fixedClient && <p>{t('line_wizard.line.what_is')}</p>}
                <p>{t('line_wizard.line.subtitle')}</p>
            </div>

            <fieldset className="lw-card lw-line" disabled={locked}>
                <div className="lw-field">
                    <label htmlFor="lw-line-name">{t('line_wizard.line.name_label')}</label>
                    <input id="lw-line-name" type="text" value={line.name}
                           placeholder={t('line_wizard.line.name_placeholder')}
                           onChange={event => setLine({name: event.target.value})}/>
                    {showErrors && !nameOk && <p className="lw-error">{t('line_wizard.line.name_missing')}</p>}
                </div>

                <div className="lw-row">
                    <div className="lw-field">
                        <label>{t('line_wizard.line.budget_label')}</label>
                        <BudgetValueInput
                            hideCurrency={!defaults.canEditCurrency}
                            canEditCurrency={defaults.canEditCurrency}
                            onCurrencyChange={value => setLine({currency: value})}
                            onAmountChange={value => setLine({budget: value})}
                            budgetCurrency={currency}
                            budgetAmount={line.budget}
                            numberLocale={AMOUNT_LOCALE}
                            amountClassName="lw-amount"
                        />
                        {showErrors && !budgetOk && <p className="lw-error">{t('line_wizard.line.budget_missing')}</p>}
                    </div>
                    <div className="lw-field lw-period">
                        <label>{t('line_wizard.line.period_label')}</label>
                        {/* Sin el día del mes, aunque la agencia lo tenga habilitado: el mensual
                            nace el 1 (`defaultSelectedPeriodicRangeDefinition`). */}
                        <BusinessUnitPeriodField
                            initialPeriod={line.period}
                            dateLocale={dateLocale}
                            hidePeriodicRangeDefinition={true}
                            onChange={value => setLine({period: value})}/>
                    </div>
                </div>

                {/* Con la línea ya creada no hay salida: lo que se armara allá sería OTRA línea. */}
                {!locked && (
                    <p className="lw-help lw-budget-type">
                        {t('line_wizard.line.budget_type_hint')}{' '}
                        <button type="button" className="ob-link lw-budget-type__link"
                                onClick={() => fullEditor.current && fullEditor.current.toggle()}>
                            {t('line_wizard.line.budget_type_link')}
                        </button>
                    </p>
                )}

                {/* Los tramos se prenden con el interruptor del propio componente: envolverlo en un
                    desplegable con el mismo rótulo decía lo mismo dos veces (QA-VIS-wizard-07).
                    `compact` es la variante de la 2.1: «Cómo funciona» sin corchetes. */}
                <BudgetPartialGoalsConfiguration
                    compact={true}
                    enabled={line.partialGoals.length > 0}
                    budgetPeriodType={period.type}
                    budgetFromDate={firstDate}
                    budgetToDate={lastDate}
                    budgetPartialGoals={line.partialGoals}
                    inputDefinition={line.partialGoalsInputDefinition}
                    onConfigurationChange={payload => setLine({
                        partialGoalsInputMode: payload.inputMode,
                        partialGoals: payload.budgetPartialGoals || [],
                        partialGoalsInputDefinition: payload.inputDefinition || null,
                    })}
                    budgetTotalAmount={line.budget}
                    allowDailyRangeMode={true}
                    numberLocale={AMOUNT_LOCALE}
                    dateLocale={dateLocale}
                />

                <h2 className="lw-section-title">{t('line_wizard.line.goals_title')}</h2>
                <p className="lw-help lw-goals-help">{t('line_wizard.line.goals_help')}</p>
                {[0, 1].map(index => (
                    <div className="lw-goal-config" key={index}>
                        <GoalConfiguration
                            priority={index + 1}
                            data={line.goals[index]}
                            adAccountsActionData={emptyActionData}
                            onChange={goal => setLine({goals: line.goals.map((current, i) => i === index ? goal : current)})}
                        />
                    </div>
                ))}
                {showErrors && !goalsOk && <p className="lw-error">{t('line_wizard.line.goals_incomplete')}</p>}

                {/* Plegado al pie y con el caso dicho en palabras: «Configurar qué días corren las
                    campañas» se leía como algo a completar (QA de fede, 02/10). Un error de días se
                    abre solo: un error escondido es un «Siguiente» que no avanza sin decir por qué. */}
                <button type="button" className="lw-advanced-toggle" aria-controls="lw-advanced"
                        aria-expanded={advancedVisible} onClick={() => setAdvancedOpen(!advancedVisible)}>
                    <CaretDown size={13} weight="bold" aria-hidden="true"/>
                    {t('line_wizard.line.advanced_toggle')}
                </button>
                <div id="lw-advanced" className="lw-advanced" hidden={!advancedVisible}>
                    <h2 className="lw-section-title">{t('line_wizard.line.days_title')}</h2>
                    <p className="lw-help lw-days-help">{t('line_wizard.line.days_help')}</p>
                    {/* El interruptor A LA IZQUIERDA y el rótulo después, como el de los tramos y los
                        de los objetivos (punto 6 del 01/10). */}
                    <div className="lw-switch-row">
                        <Switch name="lw-days" checked={line.schedule.enabled} messageToAlertUserIfChecked={null}
                                aria-labelledby="lw-days-label"
                                onChange={event => setLine({schedule: {...line.schedule, enabled: event.target.checked}})}
                                style={{margin: 0}}/>
                        <span id="lw-days-label">{t('line_wizard.line.days_switch')}</span>
                    </div>
                    {line.schedule.enabled && (
                        <div className="lw-days">
                            {WEEKDAYS.map(day => (
                                <button key={day} type="button" aria-pressed={!!line.schedule.days[day]}
                                        className={`lw-day${line.schedule.days[day] ? ' -on' : ''}`}
                                        onClick={() => setLine({schedule: {...line.schedule,
                                            days: {...line.schedule.days, [day]: !line.schedule.days[day]}}})}>
                                    {t(`line_wizard.line.day_${day}`)}
                                </button>
                            ))}
                        </div>
                    )}
                    {scheduleIsCustom(line.schedule) && <p className="lw-help">{t('line_wizard.line.days_custom')}</p>}
                    {showErrors && !daysOk && <p className="lw-error">{t('line_wizard.line.days_missing')}</p>}
                </div>
            </fieldset>

            <div className="lw-foot">
                <span className="lw-foot__spacer"/>
                {/* Entrando desde un cliente no hay paso anterior: volver es volver al cliente. */}
                <button type="button" className="ob-btn -ghost"
                        onClick={() => (fixedClient ? leave() : navigate(wizardPath('cliente')))}>
                    <ArrowLeft size={16}/> {t('line_wizard.back')}
                </button>
                <button type="button" className="ob-btn -primary" onClick={next}>
                    {t('line_wizard.next')} <ArrowRight size={16}/>
                </button>
            </div>

            {/* Para ESE cliente: con uno existente el selector del editor viene fijo; con uno nuevo
                (que todavía no existe) el editor deja elegirlo o crearlo. Y con lo ya cargado acá:
                nombre, presupuesto y período no se vuelven a escribir (review C-rec-fe-linea-05). El
                editor monta su formulario recién al abrirse, así que lee lo que haya en ese momento. */}
            {!locked && (
                <AddBusinessUnitModal ref={fullEditor}
                                      clientId={existingClientId || undefined}
                                      clientName={existingClientId ? clientName : undefined}
                                      initialValues={{name: line.name, budget: line.budget,
                                                      currency: line.currency || defaults.currency,
                                                      period: line.period || undefined}}
                                      history={{push: to => navigate(to), replace: to => navigate(to, {replace: true})}}
                                      afterSuccessfulSubmit={afterFullEditor}/>
            )}
        </div>
    );
}

/** El primer y el último día del período, que es lo que necesitan los tramos para validarse. */
function periodDates(period) {
    if (period && period.type === BU_PERIOD_TEMPORARY) {
        const range = period.range_definition || {};
        return {
            firstDate: range.from_date ? DatetimeHelper.friendlyDDMMYYYYToDate(range.from_date, '-') : null,
            lastDate: range.to_date ? DatetimeHelper.friendlyDDMMYYYYToDate(range.to_date, '-') : null,
        };
    }
    const firstDay = period && period.type === BU_PERIOD_PERIODIC && period.range_definition
        ? period.range_definition.first_day : 1;
    return DatetimeHelper.calculateDatesForMonthlyPeriod(firstDay);
}
